import { useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useAccessToken } from '../auth/use-access-token.js';
import { useServices } from '../api/use-catalogue.js';
import { useMeContext } from '../api/me-provider.js';
import { useSlot } from '../api/use-slot.js';
import { usePeople } from '../api/use-people.js';
import { addCartLine } from '../api/use-cart.js';
import { useCartContext } from '../api/cart-provider.js';
import { when } from '../components/when.js';
import { CartSubmit } from '../components/cart-cta.js';
import { ApiError } from '../api/client.js';
import { useWaivers } from '../api/use-waivers.js';
import { fillWaiver } from '../api/fill-waiver.js';
import { BookingWaivers } from '../components/booking-waivers.js';
import { WaitNote } from '../components/wait-note.js';
import { hourProducts, type HourProduct } from '../components/hour-products.js';

/**
 * One hour — what kind of lesson, and who is coming.
 *
 * TWO SCREENS IN ONE ROUTE, decided by the hour itself. An unclaimed hour asks
 * what kind of lesson first, because choosing the product is what claims it. An
 * hour somebody has already booked asks only who is coming: the kind is settled
 * and there is no choice left to offer.
 *
 * The remaining places cap the checkboxes either way, so a parent cannot choose
 * two children for one place and find out at checkout.
 */
export function SlotRoute() {
  const { slotId } = useParams();
  // From the team's assessment times (board B2), the chevron goes back there.
  const fromTimes = useSearchParams()[0].get('from') === 'assessments';
  const navigate = useNavigate();
  const getToken = useAccessToken();

  const services = useServices();
  const { state: meState } = useMeContext();
  const people = usePeople();
  const { state: cartState, reload: reloadCart } = useCartContext();

  // ONE HOUR, ASKED FOR BY ID. This used to ask /api/slots for a 183-day
  // window and pick one row out of it, because the calendar's week is not in
  // the URL. That route caps a window at 31 days, so every call was refused
  // and every hour reported itself gone — nobody could book anything on a
  // deployed build until 25 September.
  const cartId = cartState.status === 'ready' ? cartState.cart?.cart_id : undefined;
  const { state: slotState } = useSlot(slotId, cartId ?? null);

  const [productId, setProductId] = useState<string | null>(null);
  const [chosen, setChosen] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [approved, setApproved] = useState<string[]>([]);
  const [missing, setMissing] = useState<string[]>([]);
  const { state: waiverState, reload: reloadWaivers } = useWaivers();
  const waiver = waiverState.status === 'ready' ? waiverState.waivers.participation : null;
  const profile = meState.status === 'ready' ? meState.me.profile : undefined;
  const guardian = profile ? `${profile.first_name} ${profile.last_name}` : '';

  const slot = slotState.status === 'ready' ? slotState.slot : undefined;

  // WHERE THEY LIVE DECIDES WHAT THEY CAN BUY, and so the currency and the
  // price. The catalogue returns every country's services deliberately —
  // browsing does not require a finished profile, so filtering on the server
  // would change the shape of the answer depending on how far through signing
  // up you were, and list-services.ts says as much: "The country is returned
  // and the portal filters." The portal never did. A parent in Virginia was
  // shown the US and Canadian catalogues side by side: four products where
  // there are two, priced differently, indistinguishable on screen.
  // The FAMILY's country: a second parent's own may differ (canvas row D).
  const country = meState.status === 'ready' ? (meState.me.profile?.account_country ?? null) : null;

  const sold =
    services.status === 'ready' && country !== null
      ? services.value.filter((service) => service.country === country)
      : [];

  // A Lessons hour offers every product, Assessment among them; an
  // Assessments hour only the Assessment (032; boards B3, B4).
  const allProducts: HourProduct[] = slot === undefined ? [] : hourProducts(sold, slot.for_assessments);

  const claimed = slot?.claimed_tier_id
    ? allProducts.find((entry) => entry.product.tier_id === slot.claimed_tier_id)
    : undefined;

  // An Assessments hour has one thing it can be, so nothing to choose (board B3).
  const only = !claimed && slot?.for_assessments && allProducts.length === 1 ? allProducts[0] : undefined;
  const settled = claimed ?? only;
  const selected = settled ?? allProducts.find((entry) => entry.product.tier_id === productId);

  // An unclaimed hour holds as many as the chosen product allows; a claimed one
  // holds what is left in it.
  const places = slot?.seats_free ?? selected?.product.capacity ?? 0;

  // UNTIL A LESSON TYPE IS SETTLED THE HOUR HAS NO SIZE. It is not full — it is
  // undecided, and the screen says nothing about it. Before this, `places` fell
  // back to 0, every athlete came out "full", and an empty hour told a parent
  // "no places left" about children nobody had booked.
  const sized = selected !== undefined;
  // A reserved hour takes only the family's members on its teams (board B3).
  const everyone = people.state.status === 'ready' ? people.state.people : [];
  // Booking an Assessment, whoever has one already is left out (one per
  // athlete, ever - Matthew, 28 October).
  const assessing = selected?.isAssessment === true;
  const athletes = (
    slot && slot.reserved_for.length > 0 ? everyone.filter((person) => slot.team_member_ids.includes(person.client_id)) : everyone
  ).filter((person) => !(assessing && slot?.assessed_ids.includes(person.client_id)));

  // A new waiver version is approved here, when a child is next added to a
  // lesson (ruled 13 October). Never the account holder: adult waivers are parked.
  // Not for a second parent (canvas row D, ruled 17 October): they book a
  // child whose waiver is not signed, and the first parent is asked by email.
  const second = meState.status === 'ready' && meState.me.profile?.role === 'second';
  const needingWaiver = waiver && !second
    ? athletes.filter(
        (person) =>
          chosen.includes(person.client_id) && !person.is_account_holder && !person.waiver_current,
      )
    : [];

  // meState is waited on too: the country decides which catalogue this is, so
  // rendering before it arrives would show one list and then swap it.
  if (
    slotState.status === 'loading' ||
    services.status === 'loading' ||
    meState.status === 'loading'
  ) {
    return <WaitNote className="empty">Checking…</WaitNote>;
  }

  // A FAILED READ IS NOT A FACT ABOUT THE HOUR. Before this, anything that was
  // not a found slot rendered as "that hour has gone" — so a refused request
  // told the parent something about the world that nobody had established.
  if (slotState.status === 'failed') {
    return (
      <section className="panel">
        <h2>That did not load</h2>
        <p className="empty">{slotState.message}</p>
        <Link className="primary" to="/booking">
          Back to the calendar
        </Link>
      </section>
    );
  }

  if (slotState.status === 'gone' || !slot) {
    return (
      <section className="panel">
        <h2>That hour has gone</h2>
        <p className="empty">Somebody booked it, or it was taken off the calendar.</p>
        <Link className="primary" to="/booking">
          Back to the calendar
        </Link>
      </section>
    );
  }

  function toggle(clientId: string) {
    setChosen((current) =>
      current.includes(clientId)
        ? current.filter((id) => id !== clientId)
        : current.length < places
          ? [...current, clientId]
          : current,
    );
  }

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (saving || !selected || chosen.length === 0 || !slot) return;

    // Checked here as well as by the API, so the message is under the card at
    // once rather than after a round trip.
    const unapproved = needingWaiver
      .map((person) => person.client_id)
      .filter((clientId) => !approved.includes(clientId));
    setMissing(unapproved);
    if (unapproved.length > 0) return;

    setSaving(true);
    setError(null);

    const consentFor = (athleteClientId: string) => {
      const person = needingWaiver.find((each) => each.client_id === athleteClientId);
      return person && waiver
        ? {
            waiverId: waiver.waiver_id,
            text: fillWaiver(waiver.template, guardian, `${person.first_name} ${person.last_name}`),
          }
        : null;
    };

    try {
      // One line per athlete, in sequence: the second depends on the first
      // having claimed the hour as this product, and firing them together
      // would race for the same seats.
      for (const athleteClientId of chosen) {
        await addCartLine(getToken, {
          publishedSlotId: slot.slot_id,
          athleteClientId,
          serviceId: selected.serviceId,
          capacityTierId: selected.product.tier_id,
          consent: consentFor(athleteClientId),
        });
      }
      reloadCart();
      navigate('/cart');
    } catch (cause) {
      // Anything already added stays in the cart — the parent sees it there
      // rather than losing it, which is why this says what happened instead of
      // pretending nothing did.
      if (cause instanceof ApiError && cause.code === 'waiver_changed') {
        // The text changed while the screen was open. Show the new words and
        // ask again, rather than signing the old ones.
        reloadWaivers();
        setApproved([]);
      }
      if (cause instanceof ApiError && cause.code === 'waiver_needed') people.reload();
      setError(messageFor(cause));
      setSaving(false);
      reloadCart();
    }
  }

  const cart = cartState.status === 'ready' ? cartState.cart : null;

  return (
    <>
      <div className="page-head">
        <Link
          className="back"
          to={fromTimes ? '/booking/assessments' : '/booking'}
          aria-label={fromTimes ? 'Back to the assessment times' : 'Back to the calendar'}
        >
          ‹
        </Link>
        <div>
          <h2>{when(slot.slot_start)}</h2>
          <span className="detail">
            Coach {slot.coach_name} · {slot.facility_name}
          </span>
        </div>
      </div>

      <form onSubmit={submit}>
        {/* An Assessments hour is only ever an Assessment for one person: the
            screen is the person to pick and Add to cart, nothing else (Matthew,
            28 October: "the only reason to go to this page is to pick the
            person, lose all the other fluff"). */}
        {only ? null : settled ? (
          <div className="panel">
            <span className="section-label">{settled.product.name}</span>
            <p className="empty">
              {places === 1 ? 'One place left in this hour.' : `${places} places left in this hour.`}
            </p>
          </div>
        ) : (
          <>
            <span className="section-label">What kind of lesson</span>
            {allProducts.map((entry) => (
              <label key={entry.product.tier_id} className="choice">
                <input
                  type="radio"
                  name="product"
                  value={entry.product.tier_id}
                  checked={productId === entry.product.tier_id}
                  onChange={() => {
                    setProductId(entry.product.tier_id);
                    setChosen([]);
                  }}
                />
                <span className="choice-text">
                  <span className="name">{entry.product.name}</span>
                  {entry.named ? <span className="detail">{entry.serviceName}</span> : null}
                </span>
                <span className="places">
                  {entry.product.capacity === 1 ? '1 place' : `up to ${entry.product.capacity}`}
                </span>
              </label>
            ))}
          </>
        )}

        {only ? null : <span className="section-label">Who is coming</span>}

        {/* A failed read is not "nobody on this account" either (ruled 17
            October, after a removed second parent was told to add a child). A
            second parent cannot add one, so is not offered the link. */}
        {people.state.status === 'failed' ? (
          <p className="empty">That did not load</p>
        ) : people.state.status === 'ready' && everyone.length === 0 ? (
          second ? (
            <p className="empty">Nobody on this account yet.</p>
          ) : (
            <p className="empty">
              Nobody on this account yet. <Link to="/account/people/new">Add a child</Link> first.
            </p>
          )
        ) : null}

        {athletes.map((person) => {
          const isChosen = chosen.includes(person.client_id);
          const full = sized && !isChosen && chosen.length >= places;
          return (
            <label key={person.client_id} className={full ? 'choice full' : 'choice'}>
              <input
                type="checkbox"
                checked={isChosen}
                disabled={!sized || full}
                onChange={() => toggle(person.client_id)}
              />
              <span className="choice-text">
                <span className="name">
                  {person.first_name} {person.last_name}
                </span>
              </span>
              {full ? <span className="places">no places left</span> : null}
            </label>
          );
        })}

        {waiver && needingWaiver.length > 0 ? (
          <BookingWaivers
            people={needingWaiver}
            waiver={waiver}
            guardian={guardian}
            approved={approved}
            onChange={(clientId, next) => {
              setApproved((current) =>
                next ? [...current, clientId] : current.filter((id) => id !== clientId),
              );
              if (next) setMissing((current) => current.filter((id) => id !== clientId));
            }}
            missing={missing}
          />
        ) : null}

        {error ? <p className="state-note">{error}</p> : null}

        <CartSubmit
          label="Add to cart"
          expiresAt={cart?.expires_at}
          disabled={saving || chosen.length === 0 || !selected || waiverState.status === 'loading'}
          busy={saving}
          working="Adding…"
        />
      </form>
    </>
  );
}

function messageFor(cause: unknown): string {
  if (!(cause instanceof ApiError)) return 'That did not save. Try again.';

  if (cause.code === 'slot_not_bookable') return 'That hour has just gone.';
  if (cause.code === 'already_in_cart') return 'That athlete is already in the cart for this hour.';
  if (cause.code === 'athlete_not_linked') return 'That athlete is not on your account.';
  if (cause.code === 'waiver_changed') return 'The wording has changed. Read it again and approve.';
  if (cause.code === 'waiver_needed') return 'Approve before adding.';
  return 'That did not save. Try again.';
}
